"use client";

import { Metadata } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

export const metadata: Metadata = {
  title: "Peerquik: Next.js Boilerplate for B2C & P2P Marketplaces",
  description: "Build B2C and P2P marketplaces fast with Peerquik, the ultimate Next.js boilerplate. Multi-user auth, Tailwind CSS, and scalable features for your 2025 app.",
};

export default function Home() {
  const searchParams = useSearchParams();

  const buildUrlWithParams = (hash: string) => {
    const currentParams = searchParams?.toString();
    const cleanHash = hash.replace('#', '');
    return currentParams ? `/?${currentParams}#${cleanHash}` : `/#${cleanHash}`;
  };

  return (
    <div className="bg-gray-800 text-white">
      <Head>
        <title>PeerQuik: Next.js Boilerplate for B2C Marketplaces & B2B SaaS</title>
        <meta
          name="description"
          content="Launch B2C marketplaces or B2B SaaS fast with PeerQuik, the ultimate Next.js boilerplate. Multi-user auth, Stripe payments, Tailwind CSS, and scalable features for 2025."
        />
      </Head>

      {/* Hero */}
      <section className="py-10 border-b border-gray-800">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-10">
            {/* Contenido de texto y botón */}
            <div className="text-center md:text-left md:w-1/2">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Your P2P Marketplace in <span className="text-cyan-400 underline">Days</span>, not Months
                {/* Peerquik: Next.js Boilerplate for <span className="text-cyan-400 underline">B2C & P2P Marketplaces</span> in Days */}
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mb-8">
                PeerQuik is the ultimate Next.js boilerplate for <strong>B2C marketplaces</strong> and <strong>B2B SaaS</strong>. With multi-user auth, Stripe payments, and scalable features, save <strong>500+ hours</strong>.
              </p>
              <a
                href={buildUrlWithParams("#demo")}
                className="inline-block bg-cyan-500 text-black hover:text-black font-medium py-3 px-6 rounded-md hover:bg-cyan-400 transition"
              >
                Launch Your B2C/B2B SaaS Now
              </a>
              <div className="mt-8 inline-block  text-white font-medium text-md px-4 py-2 rounded-lg shadow-md transition-transform duration-300 hover:scale-105 border border-emerald-300">
                Now 1 month of {' '}
                <Link
                  href="https://seozast.com"
                  target="_blank"
                  // rel="noopener noreferrer"
                  className="text-emerald-600 hover:underline font-medium"
                >
                  SeoZast
                </Link>{' '}
                free, to boost your SEO!
              </div>

            </div>

            {/* Imagen */}
            <div className="md:w-1/2 flex justify-center">
              <img
                src="/images/peerquik-photo.png"
                width="700"
                alt="Peerquik Next.js Boilerplate for B2C Marketplaces"
                className="max-w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-6 bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center mb-6 flex-wrap">
              <div className="flex items-center -space-x-4">
                {[
                  '/images/avatars/liam.png',
                  '/images/avatars/alex.png',
                  '/images/avatars/maya.png',
                  '/images/avatars/fatima.png',
                  '/images/avatars/priya.png',
                ].map((src, index) => (
                  <div
                    key={index}
                    className="relative group cursor-pointer"
                    style={{ zIndex: 50 - index }}
                  >
                    <img
                      src={src}
                      alt={`Indie Maker ${index + 1}`}
                      className="w-10 h-10 rounded-full border-2 border-white shadow-lg 
                         transform transition-all duration-300 ease-in-out
                         group-hover:scale-110 group-hover:z-50 group-hover:shadow-2xl"
                    />
                    {/* Subtle glow effect on hover */}
                    {/* <div className="absolute inset-0 rounded-full bg-emerald-400 opacity-0 
                            group-hover:opacity-20 transition-opacity duration-300 blur-sm"></div> */}
                  </div>
                ))}
              </div>
              <p className="text-sm text-white font-semibold ml-4 max-w-[2400px] leading-tight text-left">
                5 Indie Makers Are Already Using This
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Peerquik Section */}
      <section className="py-10 border-b border-gray-800">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">Why Peerquik?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-gray-700 rounded-md">
              <h3 className="text-xl font-semibold mb-4">Not a Generic Boilerplate</h3>
              <p className="text-gray-300">
                One built for B2C marketplaces with buyer-seller logic and another for B2B SaaS (unlike standard Next.js boilerplates).
              </p>
            </div>
            <div className="p-6 bg-gray-700 rounded-md">
              <h3 className="text-xl font-semibold mb-4">Multi-User Support</h3>
              <p className="text-gray-300">
                Separate flows for buyers and sellers, ready to power your B2C and P2P apps.
              </p>
            </div>
            <div className="p-6 bg-gray-700 rounded-md">
              <h3 className="text-xl font-semibold mb-4">Scalable for B2C & B2B</h3>
              <p className="text-gray-300">
                From transactions to search, Peerquik delivers features for B2C/B2B success.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}